<template>
	<footer
		class="check-footer"
		:class="{
			disabled: isCartUpdating,
			'check-footer--dark': darkTheme
		}"
	>
		<div
			class="check-footer__top"
			:class="{ 'check-footer__top--redemption': isTabOpenedByRedemption }"
		>
			<div v-if="isTabOpenedByRedemption" class="check-footer__top__left">
				Tab #{{ openedTab.dayOrdinalNumber }}
			</div>
			<div class="check-footer__top__center">
				<div v-if="autoCloseoutText" class="check-footer-auto-closeout">
					default {{ autoCloseoutText }}
				</div>
			</div>
			<div class="check-footer__top__right"></div>
		</div>
		<div v-if="!isLoading" class="check-footer-holder">
			<payment-method-popup
				:credit-card="creditCard"
				:dark-theme="darkTheme"
				@open-credit-card-modal="$emit('open-credit-card-modal')"
			/>
			<primary-button
				v-if="isTabOpenedByRedemptionEmpty"
				class="close-tab-btn"
				large
				@click="$emit('close-empty-tab')"
			>
				<span class="button-inner">
					Close your <span class="semibold">SMART TAB</span>
				</span>
			</primary-button>
			<button
				v-else
				class="button check-pay-button"
				:class="{ loading: isWaitingForAnswer || isWaitingForPos }"
				@click="onPayClick"
			>
				<template v-if="!isWaitingForAnswer && !isWaitingForPos">
					<span>
						Pay -
						<formatted-price
							class="check-pay-button__price"
							:price="
								parseFloat(this.openedTab.amountWithSmartTabFee) +
									parseFloat(this.tip)
							"
							decimal-height="60"
						/>
					</span>
				</template>
				<span v-else class="loading-text">
					LOADING ...
				</span>
			</button>
		</div>
		<div v-else :key="'footer-frame'" class="check-footer-frame">
			<span class="processing-title">
				Your payment is currently processing...
			</span>
			<span class="processing-sub-title">
				It may take some additional time to complete this particular transaction
			</span>
		</div>

		<NoTipsModal
			v-if="isNoTipsModalVisible"
			dark-theme
			@close="hideNoTipsModal"
		/>

		<portal v-if="isMounted" to="modal">
			<transition name="fade">
				<unavailability-modal
					v-if="isUnAvailableModalOpen"
					v-bind="unAvailableModalProps"
					@on-action="onModalAction"
					@close="closeUnAvailableModal"
				/>
				<pick-up-time-modal
					v-if="isPickUpTimePickerVisible"
					:pick-up-time="pickUpTime"
					:pick-up-time-from="pickupTimeFrom"
					:pick-up-time-to="pickupTimeTo"
					@change-pick-up-time="changePickUpTime"
					@cancel="pickUpTimeModalCancel"
					@close="hidePickUpTimeModal"
				/>
				<generous-tip-modal
					v-if="isTipsModalOpen"
					dark-theme
					:price="tempTip"
					:percent="tempTipPercent"
					:is-loading="isTipsProcessing"
					@confirm-tips="confirmTips"
					@close="closeTipsModal"
				/>
			</transition>
		</portal>

		<PaymentErrorModal
			v-if="isPaymentErrorModalVisible"
			dark-theme
			@close="updateVenueSettings"
		/>
	</footer>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import PaymentMethodPopup from './PaymentMethodPopup'
import NoTipsModal from '@/components/modals/NoTipsModal'
import PaymentErrorModal from '@/components/modals/PaymentErrorModal'
import GenerousTipModal from '@/components/modals/GenerousTipModal'
import UnavailabilityModal from '@/components/modals/UnavailabilityModal.vue'
import PickUpTimeModal from '@/components/modals/PickUpTimeModal'

import pickUpValidation from '@/mixins/pickUpValidation'
import routerMixin from '@/mixins/routerMixin'

import {
	ALL_ORDER_TYPES_NAMES,
	AUTO_CLOSEOUT_TYPES,
	CART_STATUS,
	DEFAULT_UNAVAILABLE_MODAL_PROPS,
	ORDERS_ACTIONS_TEXT_LIST,
	PRODUCT_ORDER_TYPES,
	TIME_FORMATS
} from '@/constants'
import { getFormattedDate } from '@/utils/helpers'
import moment from 'moment'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import FormattedPrice from '@/components/FormattedPrice.vue'

export default {
	name: 'CheckFooter',

	mixins: [pickUpValidation, routerMixin],

	props: {
		total: {
			type: Number,
			default: 0
		},

		setIsPaymentReqProcessing: {
			type: Function,
			default: () => {}
		},

		isTabOpenedByRedemption: {
			type: Boolean,
			default: false
		},

		isTabEmpty: {
			type: Boolean,
			default: false
		},

		darkTheme: {
			type: Boolean,
			default: false
		}
	},

	components: {
		FormattedPrice,
		PrimaryButton,
		UnavailabilityModal,
		PickUpTimeModal,
		PaymentMethodPopup,
		GenerousTipModal,
		NoTipsModal,
		PaymentErrorModal
	},

	data() {
		return {
			isMounted: false,
			isLoading: false,
			isWaitingForAnswer: false,
			isNoTipsModalVisible: false,
			tip: 0,
			tipPercent: 0,
			tempTip: 0,
			tempTipPercent: 0,
			tempUseTipsPercent: false,
			currentPickUpTimeFrom: null,
			currentPickUpTimeTo: null,
			isCartUpdating: false,
			cartUpdateInterval: null,
			isPickUpTimePickerVisible: false,
			isUnAvailableModalOpen: false,
			sessionCartInterval: null,
			unAvailableModalProps: { ...DEFAULT_UNAVAILABLE_MODAL_PROPS },
			isTipsProcessing: false,
			isTipsModalOpen: false,
			isPaymentErrorModalVisible: false,
			timeToAutoCloseOut: null,
			timeToAutoCloseoutTimer: null,
			timeout: null
		}
	},

	created() {
		if (this.isWaitingForPos) this.setSessionCartInterval()

		this.initTips()
		const isSetPickUpWithOutGap = true
		this.setAllOrderingAvailable(isSetPickUpWithOutGap)

		this.currentPickUpTimeFrom = this.pickupTimeFrom
		this.currentPickUpTimeTo = this.pickupTimeTo
	},

	mounted() {
		this.isMounted = true
		this.checkAutoCloseout()
	},

	beforeDestroy() {
		clearInterval(this.cartUpdateInterval)
		this.resetTimeToAutoCloseoutTimer()

		if (this.sessionCartInterval) {
			this.clearSessionInterval()
		}

		if (this.timeout) clearTimeout(this.timeout)
	},

	computed: {
		...mapState('cart', {
			creditCardId: 'creditCardId',
			cartId: 'id',
			cartItems: 'cartItems',
			status: 'status',
			isInVenue: 'isInVenue',
			isPickUp: 'isPickUp',
			checkTips: 'tips',
			cartTipsPercent: 'tipsPercent',
			useTipsPercent: 'useTipsPercent',
			pickupTimeFrom: 'pickupTimeFrom',
			pickupTimeTo: 'pickupTimeTo',
			isCartRecreated: 'isCartRecreated',
			isSentToTab: 'sentToTab'
		}),

		...mapState('venue', [
			'pickUpTime',
			'pickUpHours',
			'isPickUpOrderingAvailable',
			'isForHereOrderingAvailable',
			'isToGoOrderingAvailable',
			'toGoHours',
			'forHereHours',
			'currentDow',
			'venueInfo'
		]),
		...mapGetters('venue', ['venueSettings', 'isPickUpTimeAvailable']),
		...mapState('payment', ['creditCards']),
		...mapGetters('cart', [
			'openedTab',
			'sumForTips',
			'cartTips',
			'cartTipsPercent'
		]),

		autoCloseOutTypeMinutes() {
			return (
				this.venueSettings.settingsCreditCard?.autoCloseoutOnlineOrderType ===
				AUTO_CLOSEOUT_TYPES.MINUTES
			)
		},

		timeAutoCloseoutOnlineOrder() {
			return (
				this.venueSettings.settingsCreditCard?.timeAutoCloseoutOnlineOrder ||
				null
			)
		},

		autoCloseoutText() {
			const { settingsCreditCard } = this.venueSettings

			if (settingsCreditCard) {
				const {
					autoCloseoutOnlineOrderType,
					autoCloseoutTime,
					autoTip
				} = settingsCreditCard

				const isAutoCloseOutTypeEndDay =
					autoCloseoutOnlineOrderType === AUTO_CLOSEOUT_TYPES.END_DAY

				if (
					isAutoCloseOutTypeEndDay ||
					autoCloseoutOnlineOrderType === AUTO_CLOSEOUT_TYPES.AUTO_CLOSEOUT_TIME
				) {
					const time = moment(
						(isAutoCloseOutTypeEndDay && this.venueInfo.businessDays.eodSun) ||
							autoCloseoutTime,
						'hh:mm:ss'
					)
					const formattedTime = getFormattedDate(time, null, TIME_FORMATS.AMPM)

					return `${autoTip}% tip at ${formattedTime}`
				} else if (this.timeToAutoCloseOut) {
					return `${autoTip}% tip ${this.timeToAutoCloseOut}`
				}
			}

			return ''
		},

		creditCard() {
			return this.creditCards.find(item => item.id === this.creditCardId)
		},

		tipsSettings() {
			return this.venueSettings.tipsSettings
		},

		acceptTips() {
			return !this.tipsSettings.dontAcceptTips
		},

		tipPercentValues() {
			const { tip1, tip2, tip3 } = this.tipsSettings
			return [tip1, tip2, tip3]
		},

		isPaymentSuccess() {
			return this.status === CART_STATUS['PAYMENT_SUCCESS']
		},

		isPaymentError() {
			return this.status === CART_STATUS['PAYMENT_FAILED']
		},

		isWaitingForPos() {
			return this.status === CART_STATUS['WAITING_POS']
		},

		isTabOpenedByRedemptionEmpty() {
			return Boolean(this.isTabEmpty && this.isTabOpenedByRedemption)
		}
	},

	watch: {
		total() {
			this.initTips()
		},

		isPaymentError() {
			if (this.isPaymentError) {
				this.addMainError('Payment failed')
				this.isLoading = false

				if (this.cartUpdateInterval) {
					clearInterval(this.cartUpdateInterval)
				}
			}
		},

		isPaymentSuccess(val) {
			if (val) {
				this.isLoading = false
			}
		},

		isUnAvailableModalOpen(val) {
			this.isWaitingForAnswer = val
		},

		status(val) {
			if (val !== CART_STATUS['WAITING_POS'] && this.sessionCartInterval) {
				this.clearSessionInterval()
			}
		},

		autoCloseOutTypeMinutes() {
			this.checkAutoCloseout()
		}
	},

	methods: {
		...mapActions('venue', [
			'setAllOrderingAvailable',
			'setPickUpTimeWithoutGap',
			'getVenueInfo'
		]),

		...mapActions('cart', [
			'payOrder',
			'setPaymentStatus',
			'getCart',
			'updateCart',
			'reschedulePickUp',
			'removeProductsByType'
		]),

		...mapActions('ui', ['addMainError']),

		...mapActions('payment', ['getCreditCards']),

		checkAutoCloseout() {
			const shouldRunTimer =
				this.autoCloseOutTypeMinutes && !this.timeToAutoCloseoutTimer

			if (shouldRunTimer) {
				this.runTimeToAutoCloseOutInterval()
			} else {
				this.resetTimeToAutoCloseoutTimer()
			}
		},

		resetTimeToAutoCloseoutTimer() {
			if (this.timeToAutoCloseoutTimer) {
				clearInterval(this.timeToAutoCloseoutTimer)
				this.timeToAutoCloseOut = null
			}
		},

		closeTipsModal() {
			this.isTipsModalOpen = false
			this.$emit('set-default-tips')
		},

		runTimeToAutoCloseOutInterval() {
			this.setTimeToAutoCloseOut()

			this.timeToAutoCloseoutTimer = setInterval(
				this.setTimeToAutoCloseOut,
				60000
			)
		},

		setTimeToAutoCloseOut() {
			const openTime = this.openedTab?.openTime
				? moment(this.openedTab?.openTime)
				: null

			if (openTime) {
				const dateNow = moment(new Date())
				const minutesAfterOpening = dateNow.diff(openTime, 'minutes')

				if (this.timeAutoCloseoutOnlineOrder) {
					const minutesLeft =
						this.timeAutoCloseoutOnlineOrder - minutesAfterOpening

					if (minutesLeft <= 0) {
						this.timeToAutoCloseOut = 'now'

						return
					}

					const duration = moment.duration(minutesLeft, 'minutes')

					const durationDays = duration.days()
					const durationHours = duration.hours()
					const durationMinutes = duration.minutes()

					const resultDurationDays = durationDays ? `${durationDays} d ` : ''
					const resultDurationHours = durationHours ? `${durationHours} h ` : ''
					const resultDurationMinutes = durationMinutes
						? `${durationMinutes} min `
						: ''

					this.timeToAutoCloseOut = `in ${resultDurationDays}${resultDurationHours}${resultDurationMinutes}`
				}
			}
		},

		clearSessionInterval() {
			if (this.sessionCartInterval) {
				clearInterval(this.sessionCartInterval)

				this.sessionCartInterval = null
			}
		},

		getAvailabilityValues() {
			let isTypeForHereAndUnavailable = false
			let isTypePickUpAndUnavailable = false
			const productsOrdersTypes = [
				...new Set(this.cartItems.map(item => item.orderType))
			]

			if (this.isInVenue) {
				for (let type of productsOrdersTypes) {
					if (
						type === PRODUCT_ORDER_TYPES.forHere &&
						!this.isForHereOrderingAvailable
					) {
						isTypeForHereAndUnavailable = true
					}
				}
			} else {
				if (
					productsOrdersTypes.includes(PRODUCT_ORDER_TYPES.pickUp) &&
					!this.isPickUpOrderingAvailable
				) {
					isTypePickUpAndUnavailable = true
				}
			}

			return {
				isTypeForHereAndUnavailable,
				isTypePickUpAndUnavailable
			}
		},

		getCurrentTypeAvailableHours(
			isTypeForHereAndUnavailable,
			isTypePickUpAndUnavailable,
			isInVenueUnavailable
		) {
			const forHerePeriod = this.forHereHours?.length && this.forHereHours[0]

			if (isInVenueUnavailable) {
				return [forHerePeriod]
			}

			if (isTypeForHereAndUnavailable) return forHerePeriod

			if (isTypePickUpAndUnavailable)
				return this.pickUpHours?.length && this.pickUpHours[0]
		},

		checkAvailability() {
			let isAvailable = true
			const {
				isTypeForHereAndUnavailable,
				isTypePickUpAndUnavailable
			} = this.getAvailabilityValues()

			if (!isTypeForHereAndUnavailable && !isTypePickUpAndUnavailable) {
				return isAvailable
			}

			const isInVenueUnavailable =
				isTypeForHereAndUnavailable && !this.isForHereOrderingAvailable

			const availablePeriod = this.getCurrentTypeAvailableHours(
				isTypeForHereAndUnavailable,
				isTypePickUpAndUnavailable,
				isInVenueUnavailable
			)

			if (isInVenueUnavailable) {
				this.unAvailableModalProps = {
					...this.unAvailableModalProps,
					actionText: ORDERS_ACTIONS_TEXT_LIST.cancel,
					action: this.cancelOrder,
					orderType: ALL_ORDER_TYPES_NAMES.inVenue,
					availablePeriod
				}

				isAvailable = false

				return isAvailable
			}

			if (isTypeForHereAndUnavailable) {
				this.unAvailableModalProps = {
					...this.unAvailableModalProps,
					actionText: ORDERS_ACTIONS_TEXT_LIST.removeForHere,
					action: () => this.removeByType(PRODUCT_ORDER_TYPES.forHere),
					orderType: ALL_ORDER_TYPES_NAMES.forHere,
					availablePeriod
				}

				isAvailable = false

				return isAvailable
			}

			if (isTypePickUpAndUnavailable) {
				this.unAvailableModalProps = {
					...this.unAvailableModalProps,
					actionText: ORDERS_ACTIONS_TEXT_LIST.cancel,
					action: this.cancelOrder,
					orderType: ALL_ORDER_TYPES_NAMES.pickUp,
					availablePeriod
				}

				isAvailable = false

				return isAvailable
			}
		},

		async removeByType(type) {
			const payload = {
				cartId: this.cartId,
				orderType: type
			}
			this.unAvailableModalProps = {
				...this.unAvailableModalProps,
				isLoading: true
			}

			try {
				await this.removeProductsByType(payload)

				if (this.isWaitingForPos) this.setSessionCartInterval()
			} catch (e) {
				this.handleCartError(e)
			} finally {
				this.unAvailableModalProps = {
					...this.unAvailableModalProps,
					isLoading: false
				}

				this.closeUnAvailableModal()
			}
		},

		setSessionCartInterval() {
			this.sessionCartInterval = setInterval(async () => {
				try {
					await this.getCart()
				} catch (e) {
					this.handleCartError(e)
				}
			}, 5000)
		},

		onModalAction(action) {
			action()
		},

		closeUnAvailableModal() {
			this.isUnAvailableModalOpen = false
		},

		initTips() {
			if (!this.acceptTips) {
				this.tipPercent = 0
				this.tip = 0
			} else if (this.useTipsPercent) {
				this.tipPercent = this.cartTipsPercent
				this.tip = (this.sumForTips * (this.tipPercent / 100)).toFixed(2)
			} else if (this.checkTips !== null && this.checkTips !== undefined) {
				this.tipPercent =
					Math.round((this.checkTips / this.sumForTips) * 100) || 0
				this.tip = this.checkTips
			} else {
				this.tipPercent = this.tipPercentValues[1]
				this.tip = (this.sumForTips * (this.tipPercent / 100)).toFixed(2)
			}
		},

		hideNoTipsModal() {
			this.isNoTipsModalVisible = false
		},

		showPickUpTimePickerModal() {
			this.isPickUpTimePickerVisible = true
		},

		hidePickUpTimeModal() {
			this.isPickUpTimePickerVisible = false
		},

		async changePickUpTime({ pickUpTimeFrom, pickUpTimeTo }) {
			this.currentPickUpTimeFrom = pickUpTimeFrom
			this.currentPickUpTimeTo = pickUpTimeTo

			this.isCartUpdating = true

			let payload = {
				cartId: this.cartId
			}

			if (this.currentPickUpTimeFrom === 'ASAP') {
				payload = {
					...payload,
					isAsap: true,
					pickupTimeFrom: null,
					pickupTimeTo: null
				}
			} else {
				payload = {
					...payload,
					isAsap: false,
					pickupTimeFrom: this.currentPickUpTimeFrom,
					pickupTimeTo: this.currentPickUpTimeTo
				}
			}

			try {
				await this.reschedulePickUp(payload)
				this.isCartUpdating = false
			} catch (e) {
				this.isCartUpdating = false
			}
		},

		pickUpTimeModalCancel() {
			this.hidePickUpTimeModal()
			this.cancelOrder()
		},

		cancelOrder() {
			this.$emit('cancel-order')
		},

		async confirmTips() {
			this.isTipsProcessing = true
			this.tip = this.tempTip || 0
			this.tipPercent = this.tempTipPercent || 0
			this.isTipsModalOpen = false

			await this.callUpdateCart(true)
			this.isTipsProcessing = false
			this.initTips()
		},

		// TODO: Move tips logic to TipSelection component
		async applyTip({ tip, percent, useTipsPercent }) {
			const {
				warnSuspTips,
				suspTipsOver,
				suspTipsOverPercent
			} = this.tipsSettings

			this.tempTip = tip
			this.tempTipPercent = percent
			this.tempUseTipsPercent = useTipsPercent

			if (
				warnSuspTips &&
				tip > suspTipsOver &&
				percent > suspTipsOverPercent &&
				!this.isTabEmpty
			) {
				this.isTipsModalOpen = true
				this.$emit('remove-tips-focus-on-IOS')
				return
			}

			this.$emit('tips-loading', true)

			this.tip = tip || 0
			this.tipPercent = percent || 0
			await this.callUpdateCart(true)

			this.initTips()
		},

		async callUpdateCart(needImmediateUpdate = false) {
			this.isCartUpdating = true

			const payload = {
				tips: +this.tip,
				tipsPercent: this.tipPercent,
				useTipsPercent: this.tempUseTipsPercent,
				pickupTimeFrom: this.currentPickUpTimeFrom,
				pickupTimeTo: this.currentPickUpTimeTo
			}

			if (needImmediateUpdate) payload.needImmediateUpdate = true

			try {
				await this.updateCart(payload)
			} catch (e) {
				this.handleCartError(e)
			} finally {
				this.isCartUpdating = false
				this.$emit('tips-loading', false)
			}
		},

		onPayClick() {
			// timeout required to apply tips
			this.timeout = setTimeout(() => {
				if (this.isCartUpdating) return

				this.handlePayClick()
			}, 300)
		},

		async handlePayClick() {
			const { subDomain, venueChainId } = this.$route.params
			this.isWaitingForAnswer = true
			this.setIsPaymentReqProcessing(true)

			try {
				await this.getCart(true)
			} catch (e) {
				this.isWaitingForAnswer = false
				this.setIsPaymentReqProcessing(false)

				return
			}

			if (this.isCartRecreated) {
				this.isWaitingForAnswer = false
				this.setIsPaymentReqProcessing(false)

				return
			}

			try {
				await this.getVenueInfo({
					venueChainSubdomain: subDomain,
					venueId: venueChainId
				})
				this.setAllOrderingAvailable()
			} catch (e) {
				this.setIsPaymentReqProcessing(false)

				return
			} finally {
				this.isWaitingForAnswer = false
			}

			if (this.isPickUp) {
				this.setPickUpTimeWithoutGap()

				const isPickUpValid = this.pickUpValidation(
					this.pickupTimeFrom,
					this.pickupTimeTo
				)

				if (!isPickUpValid && this.isPickUpTimeAvailable) {
					this.showPickUpTimePickerModal()
					this.setIsPaymentReqProcessing(false)

					return
				}
			}

			const isValid = this.checkAvailability()

			if (!isValid) {
				this.isUnAvailableModalOpen = true
				this.setIsPaymentReqProcessing(false)

				return
			}

			await this.callPayOrder()
		},

		async callPayOrder() {
			try {
				this.isLoading = true
				this.setPaymentStatus(2)

				if (this.status === CART_STATUS['NEW']) {
					this.setIsPaymentReqProcessing(false)

					return
				}

				try {
					this.setCartUpdate()
					await this.payOrder({
						cartId: this.cartId,
						tips: +parseFloat(this.tip).toFixed(2)
					})
				} catch (e) {
					this.handleCartError(e)
					this.setIsPaymentReqProcessing(false)
				}
			} catch (e) {
				this.isLoading = false
				this.setIsPaymentReqProcessing(false)
			}
		},

		setCartUpdate() {
			this.cartUpdateInterval = setInterval(async () => {
				try {
					const { data: cartData } = await this.getCart()
					const status = cartData

					if (
						status === CART_STATUS.PAYMENT_SUCCESS ||
						status === CART_STATUS.PAYMENT_FAILED ||
						status === CART_STATUS.RECEIPT_VIEWED
					) {
						clearInterval(this.cartUpdateInterval)
						this.setIsPaymentReqProcessing(false)
					}
				} catch (e) {
					this.handleCartError(e)
				}
			}, 5000)
		},

		handleCartError(e) {
			if (e && e.message === 'Network Error') {
				this.addMainError(e.message)
				return
			}
			const errorStatus = e && e.response && e.response.status

			if (errorStatus === 400) {
				this.isPaymentErrorModalVisible = true
			}
		},

		async updateVenueSettings() {
			const { subDomain, venueChainId } = this.$route.params
			try {
				this.isLoading = true
				await this.getVenueInfo({
					venueChainSubdomain: subDomain,
					venueId: venueChainId
				})

				this.$emit('open-credit-card-modal')
				this.isLoading = false
				this.isPaymentErrorModalVisible = false
				clearInterval(this.cartUpdateInterval)
			} catch (e) {
				this.isLoading = false
			}
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.check-footer {
	flex-shrink: 0;
	font-size: 14px;
	position: relative;
	padding-top: 15px;
	background: $white;
	box-shadow: 0px 1px 6px 2px rgba($black, 0.16);
	z-index: 10;

	&__top {
		display: flex;
		align-items: center;
		padding: 0 12px;
		font-size: 12px;

		&__center {
			width: 100%;
			text-align: center;
		}

		&--redemption {
			padding: 4px 12px;
			box-shadow: inset 0 -2px 6px -2px rgba($black, 0.16);

			.check-footer__top__left {
				width: 25%;
				flex-shrink: 0;
			}

			.check-footer__top__center {
				width: 50%;
				flex-shrink: 0;
			}

			.check-footer__top___right {
				flex-shrink: 0;
				width: 25%;
			}
		}
	}

	.check-footer-auto-closeout {
		color: $natural-red;
		text-align: center;
		font-size: 12px;
		letter-spacing: 0.24px;
		font-weight: 600;
		margin-bottom: 8px;
	}

	.processing-title {
		display: block;
		padding: 0 0 9px;
		font-size: 16px;
		font-weight: 800;
		color: $blue-faceless-void;
		animation: flashing 0.9s infinite;
	}

	.processing-sub-title {
		color: $grey;
		font-size: 12px;
	}

	.tip {
		position: relative;
		flex-shrink: 0;
		align-self: stretch;
		display: flex;
		align-items: center;
	}

	.tip-opener {
		display: inline-block;
		padding: 0 16px 0 0;
		position: relative;
		cursor: pointer;
		font-size: 12px;
		text-align: center;

		&:after {
			position: absolute;
			right: 0;
			top: 3px;
			width: 5px;
			height: 5px;
			border: solid $blue-faceless-void;
			border-width: 0 0 1px 1px;
			transform: rotate(-45deg);
			transition: all 0.1s linear;
			content: '';
		}

		&.no-tip:after {
			display: none;
		}

		&.opened:after {
			top: 5px;
			transform: rotate(135deg);
		}

		.mark {
			font-size: 12px;
		}
	}

	&--dark {
		background-color: $background-box;
		color: $white;

		.tip-opener {
			&:after {
				border-color: $semi-dark-blue;
			}
		}

		.check-footer__top--redemption {
			box-shadow: none;
		}

		.processing-title {
			color: $semi-dark-blue;
		}
	}
}

.check-footer-holder {
	padding: 5px 12px 15px 12px;

	@media (min-width: 767px) {
		max-width: 70%;
		margin: 0 auto;
	}

	&--with-closeout {
		padding-top: 5px;
	}
}

.check-footer-frame {
	padding: 0 12px 15px;
	text-align: center;
}

.check-pay-button {
	min-width: 150px;
	width: 100%;
	padding: 16px;
	position: relative;
	font-size: 20px;
	font-weight: 300;
	border-radius: 48px;

	&.loading {
		animation: none;
		opacity: 0.5;
	}

	&__price {
		font-weight: 600;
	}
}

.close-tab-btn {
	width: 100%;
}

@media (max-width: 767px) {
	.check-footer {
		.tip-opener {
			display: inline-flex;
			margin-top: auto;
			align-self: stretch;
			width: 64px;
			padding: 0 10px 23px;
			font-size: 12px;

			&:after {
				top: auto;
				bottom: 8px;
				border-width: 0 0 1px 1px;
				right: 50%;
				transform: translateX(1px) rotate(-45deg);
			}

			&.opened:after {
				top: auto;
				bottom: 8px;
				transform: translateX(1px) rotate(135deg);
			}

			.mark {
				display: block;
				padding: 3px 0 0;
				font-size: 10px;
			}
		}
	}
	.button-inner {
		.semibold {
			font-weight: 600;
		}
	}
}

// TODO: Need to test it and remove from 5.0.0
//@media (min-width: 768px) {
//	.close-tab-btn {
//		position: sticky;
//		left: 50%;
//		transform: translateX(-50%);
//	}
//}
</style>
